import React, { useState, useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { apiGet } from '../utils/api';
import { formatTimestamp } from '../utils/formatTime';
import DiscordIcon from './DiscordIcon';
import LoadingSpinner from './LoadingSpinner';

const PAGE_SIZE = 100;

const FILTERS = [
  { key: 'all',      icon: '📋' },
  { key: 'attack',   icon: '⚔️' },
  { key: 'defense',  icon: '🛡️' },
  { key: 'missed',   icon: '🚫' },
  { key: 'donation', icon: '💝' },
  { key: 'discord',  icon: '🎮' },
];

function buildUrl(slug, filter, limit, offset) {
  const typeParam = filter !== 'all' ? `&type=${filter}` : '';
  return `/v1/clan/${slug}/events?limit=${limit}&offset=${offset}${typeParam}`;
}

export default function EventsFeed({ t, slug, lastSyncAt }) {
  const [allEvents, setAllEvents] = useState([]);
  const [total, setTotal] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  // Reset when slug or filter changes
  useEffect(() => {
    setAllEvents([]);
    setTotal(null);
  }, [slug, activeFilter]);

  const { data: rawData, isError: error, isFetching } = useQuery({
    queryKey: ['events', slug, activeFilter],
    queryFn: () => apiGet(buildUrl(slug, activeFilter, PAGE_SIZE, 0)),
    enabled: !!slug,
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  });

  // When the first page arrives (or auto-refresh), replace the top of the list
  useEffect(() => {
    if (!rawData) return;
    const fresh = rawData.events || [];
    const newTotal = rawData.total ?? fresh.length;
    setTotal(newTotal);
    setAllEvents(prev => {
      const rest = prev.slice(PAGE_SIZE);
      return [...fresh, ...rest];
    });
  }, [rawData]);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await apiGet(buildUrl(slug, activeFilter, PAGE_SIZE, allEvents.length));
      const more = data.events || [];
      setTotal(data.total ?? allEvents.length + more.length);
      setAllEvents(prev => [...prev, ...more]);
    } finally {
      setLoadingMore(false);
    }
  };

  const hasMore = total !== null && allEvents.length < total;

  if (error) {
    return (
      <div id="eventsPage">
        <h1 className="text-2xl md:text-3xl mb-6">📊 <span>{t('page_events')}</span></h1>
        <div id="eventsFeed" className="bg-gray-800 p-4 md:p-6 rounded-xl text-center text-red-400">{t('error_loading_data')}</div>
      </div>
    );
  }

  if (allEvents.length === 0 && (!rawData || isFetching)) {
    return (
      <div id="eventsPage">
        <h1 className="text-2xl md:text-3xl mb-6">📊 <span>{t('page_events')}</span></h1>
        <div id="eventsFeed" className="bg-gray-800 rounded-xl">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  const showEmptyState = allEvents.length === 0 && !isFetching;

  return (
    <div id="eventsPage">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="text-2xl md:text-3xl">📊 <span>{t('page_events')}</span></h1>
        {lastSyncAt && (
          <div className="dash-sync-tag">🔄 {formatTimestamp(lastSyncAt)}</div>
        )}
      </div>

      <div className="ef-filter-bar">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setActiveFilter(f.key)}
            className={`ef-filter-btn${activeFilter === f.key ? ' ef-filter-btn-active' : ''}`}
          >
            <span className="ef-filter-icon">{f.icon}</span>
            <span className="ef-filter-label">{t(`ef_filter_${f.key}`)}</span>
            {activeFilter === f.key && total !== null && (
              <span className="ef-filter-count">{total}</span>
            )}
          </button>
        ))}
      </div>

      <div id="eventsFeed" className="ef-feed">
        {showEmptyState ? (
          <div className="bg-gray-800 p-8 md:p-12 rounded-xl text-center">
            <div className="text-6xl mb-4">🎯</div>
            <h2 className="text-xl font-semibold text-gray-300 mb-2">
              {activeFilter === 'all' ? (t('ef_no_events_yet') || 'No events yet this season') : (t('ef_no_filtered_events') || 'No events found for this filter')}
            </h2>
            <p className="text-gray-500 text-sm">
              {activeFilter === 'all'
                ? (t('ef_events_will_appear') || 'Events will appear here as clan members participate in wars, donations, and other activities.')
                : (t('ef_try_different_filter') || 'Try selecting a different filter to see other events.')
              }
            </p>
          </div>
        ) : (
          allEvents.map((event, i) => {
            if (event.type === 'attack') return <AttackCard key={i} event={event} t={t} />;
            if (event.type === 'discord_bonus') return <DiscordBonusCard key={i} event={event} t={t} />;
            if (event.type === 'discord_bonus_removed') return <DiscordBonusCard key={i} event={event} t={t} />;
            return <DonationCard key={i} event={event} t={t} />;
          })
        )}
      </div>
      {hasMore && (
        <div className="flex justify-center mt-6">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="px-6 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingMore ? t('loading') : `${t('load_more')} (${total - allEvents.length})`}
          </button>
        </div>
      )}
    </div>
  );
}

function WarTypePill({ warType }) {
  return warType === 'CWL'
    ? <span className="ef-pill ef-pill-cwl">CWL</span>
    : <span className="ef-pill ef-pill-cw">CW</span>;
}

function PointsBadge({ points }) {
  const text = points >= 0 ? `+${points}` : `${points}`;
  return (
    <span className={`ef-pts${points > 0 ? ' ef-pts-pos' : points < 0 ? ' ef-pts-neg' : ' ef-pts-zero'}`}>
      {text}
    </span>
  );
}

function AttackCard({ event, t }) {
  if (event.is_defense === 2) {
    return (
      <div className="ef-card ef-card-missed">
        <div className="ef-card-accent" />
        <div className="ef-card-body">
          <div className="ef-card-left">
            <span className="ef-card-action-icon">🚫</span>
            <div className="ef-card-info">
              <span className="ef-card-name">{event.player_name}</span>
              <span className="ef-card-sub">{t('missed_attack')} · <WarTypePill warType={event.war_type} /></span>
            </div>
          </div>
          <div className="ef-card-right">
            <PointsBadge points={event.points} />
            <span className="ef-card-time">{formatTimestamp(event.timestamp)}</span>
          </div>
        </div>
      </div>
    );
  }

  const stars = '⭐'.repeat(event.stars) + '☆'.repeat(3 - event.stars);
  const actionIcon = event.is_defense ? '🛡️' : '⚔️';
  const cardClass = event.is_defense
    ? (event.stars <= 1 ? 'ef-card-def-good' : 'ef-card-def-bad')
    : (event.stars >= 3 ? 'ef-card-atk-great' : event.stars >= 2 ? 'ef-card-atk-good' : 'ef-card-atk-bad');

  return (
    <div className={`ef-card ${cardClass}`}>
      <div className="ef-card-accent" />
      <div className="ef-card-body">
        <div className="ef-card-left">
          <span className="ef-card-action-icon">{actionIcon}</span>
          <div className="ef-card-info">
            <div className="ef-card-name-row">
              <span className="ef-card-name">{event.player_name}</span>
              <WarTypePill warType={event.war_type} />
            </div>
            <div className="ef-card-th-row">
              <img src={`${process.env.PUBLIC_URL}/static/img/townhall/TH${event.attacker_th}.webp`} className="ef-th-img" alt={`TH${event.attacker_th}`} />
              <span className="ef-vs">vs</span>
              <img src={`${process.env.PUBLIC_URL}/static/img/townhall/TH${event.defender_th}.webp`} className="ef-th-img" alt={`TH${event.defender_th}`} />
              <span className="ef-stars">{stars}</span>
            </div>
          </div>
        </div>
        <div className="ef-card-right">
          <PointsBadge points={event.points} />
          <span className="ef-card-time">{formatTimestamp(event.timestamp)}</span>
        </div>
      </div>
    </div>
  );
}

function DonationCard({ event, t }) {
  return (
    <div className="ef-card ef-card-donation">
      <div className="ef-card-accent" />
      <div className="ef-card-body">
        <div className="ef-card-left">
          <span className="ef-card-action-icon">💝</span>
          <div className="ef-card-info">
            <span className="ef-card-name">{event.player_name}</span>
            <span className="ef-card-sub">{t('donated_troops', { count: event.donations })}</span>
          </div>
        </div>
        <div className="ef-card-right">
          <PointsBadge points={event.points} />
          <span className="ef-card-time">{formatTimestamp(event.timestamp)}</span>
        </div>
      </div>
    </div>
  );
}

function DiscordBonusCard({ event, t }) {
  const isNegative = event.points < 0;
  return (
    <div className={`ef-card ${isNegative ? 'ef-card-discord-neg' : 'ef-card-discord'}`}>
      <div className="ef-card-accent" />
      <div className="ef-card-body">
        <div className="ef-card-left">
          <span className="ef-card-action-icon"><DiscordIcon className="w-5 h-5" /></span>
          <div className="ef-card-info">
            <span className="ef-card-name">{event.player_name}</span>
            <span className="ef-card-sub">{isNegative ? t('discord_member_removed') : t('discord_member_bonus')}</span>
          </div>
        </div>
        <div className="ef-card-right">
          <PointsBadge points={event.points} />
          <span className="ef-card-time">{formatTimestamp(event.timestamp)}</span>
        </div>
      </div>
    </div>
  );
}
